/**
 * Shared JWT Core Utilities
 *
 * Platform-agnostic JWT helpers used by both web (frontend) and mobile platforms.
 * Contains pure, side-effect-free functions for decoding, inspecting, and
 * checking expiry of JWT tokens.
 *
 * ⚠️  This module MUST NOT import any platform-specific APIs (localStorage,
 * AsyncStorage, axios, etc.).  All platform-specific logic belongs in the
 * platform adapters:
 *   - frontend/src/services/jwtService.ts   (localStorage)
 *   - mobile/src/services/jwtServiceMobile.ts (AsyncStorage)
 *
 * Usage:
 *   import { decodeJwtPayload, isTokenExpired, REFRESH_BUFFER_MS } from '@shared/services/jwtService';
 */

// ─── Constants ────────────────────────────────────────────────────────────────

/** Refresh access token this many milliseconds before it expires (1 minute). */
export const REFRESH_BUFFER_MS = 60 * 1_000;

/** Minimum interval between refresh attempts to prevent rapid retries (30 seconds). */
export const MIN_REFRESH_INTERVAL_MS = 30 * 1_000;

/** Default storage key names (used as-is in localStorage / AsyncStorage). */
export const JWT_STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  TOKEN_EXPIRY: 'tokenExpiry',
  /** Legacy Token-auth key — kept for migration support. */
  LEGACY_TOKEN: 'authToken',
} as const;

export type JwtStorageKeys = typeof JWT_STORAGE_KEYS;

// ─── Types ────────────────────────────────────────────────────────────────────

/** Minimal JWT payload shape — only the standard `exp` claim is required. */
export interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/** Tenant-related claims embedded in the JWT by the ProjectMeats backend. */
export interface JwtTenantClaims {
  tenantIds?: string[];
  defaultTenantId?: string;
  defaultTenantSlug?: string;
  defaultTenantRole?: string;
}

// ─── Core Utilities ───────────────────────────────────────────────────────────

/**
 * Decode a JWT payload segment **without** cryptographic verification.
 *
 * Safe to call in both browser and React Native environments.
 * Returns `null` on any parse/decode error rather than throwing.
 *
 * @param token - A compact-serialised JWT string (`header.payload.signature`)
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    // Normalise Base64URL → Base64
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if required
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);

    // atob is available natively in modern browsers and React Native (>= 0.67)
    const jsonPayload = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Return the expiry timestamp of a JWT as **milliseconds since the Unix epoch**.
 * Returns `null` when the token cannot be decoded or has no `exp` claim.
 *
 * @param token - A compact-serialised JWT string
 */
export function getTokenExpiry(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (payload?.exp == null) return null;
  return payload.exp * 1_000; // convert seconds → milliseconds
}

/**
 * Return `true` when the token has expired or will expire within `bufferMs`.
 * Tokens that cannot be decoded are treated as expired.
 *
 * @param token    - A compact-serialised JWT string
 * @param bufferMs - Additional buffer in milliseconds (default: 0)
 */
export function isTokenExpired(token: string, bufferMs = 0): boolean {
  const expiry = getTokenExpiry(token);
  if (expiry === null) return true;
  return Date.now() >= expiry - bufferMs;
}

/**
 * Extract the ProjectMeats-specific tenant claims from a JWT.
 * Returns `null` when no access token is provided or it cannot be decoded.
 *
 * @param token - A compact-serialised JWT string
 */
export function getTenantClaimsFromToken(token: string): JwtTenantClaims | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  return {
    tenantIds: payload.tenant_ids as string[] | undefined,
    defaultTenantId: payload.default_tenant_id as string | undefined,
    defaultTenantSlug: payload.default_tenant_slug as string | undefined,
    defaultTenantRole: payload.default_tenant_role as string | undefined,
  };
}
